import { createSelector } from 'reselect';

import {
  getAdImportDescriptionMetadata,
  getAssetBoardMembershipUpdateTasksFromTasks,
  getAssetCustomFieldValueUpdateTasksFromTasks,
  getAssetRestorationTasksFromTasks,
  getAssetTaggingTasksFromTasks,
  getAssetTransformationTasksFromTasks,
  getBoardDeletionTasksFromTasks,
  getBoardRestorationTasksFromTasks,
  getCTWTasksFromTasks,
  getDuplicateAssetTasksFromTasks,
  getDuplicateBoardTasksFromTasks,
  getImporterTasksFromTasks,
  getLibraryDeletionTasksFromTasks,
  getLibraryRestorationTasksFromTasks,
  getTranscriptEditingTasksFromTasks,
  getZippingTasksFromTasks,
} from '~/store/tasks/utils';
import { type AirState } from '~/store/types';

const baseTasksSelector = ({ tasks }: AirState) => tasks;

export const tasksSelector = createSelector(baseTasksSelector, ({ tasks }) => tasks);

export const tasksSelectorByWorkspaceId = (workspaceId?: string) =>
  createSelector(baseTasksSelector, ({ tasks }) =>
    tasks.filter((task) => !task.workspaceId || task.workspaceId === workspaceId),
  );

export const tasksSelectorByShortId = (shortId?: string) =>
  createSelector(baseTasksSelector, ({ tasks }) => tasks.filter((task) => !task.shortId || task.shortId === shortId));

export const zippingTasksSelector = createSelector(tasksSelector, (tasks) => getZippingTasksFromTasks(tasks));

export const makeZippingTasksSelectorByWorkspaceId = (workspaceId?: string) =>
  createSelector(tasksSelectorByWorkspaceId(workspaceId), (tasks) => getZippingTasksFromTasks(tasks));

export const makeZippingTasksSelectorByShortId = (shortId?: string) =>
  createSelector(tasksSelectorByShortId(shortId), (tasks) => getZippingTasksFromTasks(tasks));

export const makeCopyToWorkspaceTasksSelectorByShortId = (shortId?: string) =>
  createSelector(tasksSelectorByShortId(shortId), (tasks) => getCTWTasksFromTasks(tasks));

export const makeDuplicateBoardTasksSelectorByWorkspaceId = (workspaceId?: string) =>
  createSelector(tasksSelectorByWorkspaceId(workspaceId), (tasks) => getDuplicateBoardTasksFromTasks(tasks));

export const makeDuplicateAssetTasksSelectorByWorkspaceId = (workspaceId?: string) =>
  createSelector(tasksSelectorByWorkspaceId(workspaceId), (tasks) => getDuplicateAssetTasksFromTasks(tasks));

export const makeBoardDeletionTasksSelectorByWorkspaceId = (workspaceId?: string) =>
  createSelector(tasksSelectorByWorkspaceId(workspaceId), (tasks) => getBoardDeletionTasksFromTasks(tasks));

export const makeLibraryDeletionTasksSelectorByWorkspaceId = (workspaceId?: string) =>
  createSelector(tasksSelectorByWorkspaceId(workspaceId), (tasks) => getLibraryDeletionTasksFromTasks(tasks));

export const makeRestoreBoardTasksByWorkspaceId = (workspaceId?: string) =>
  createSelector(tasksSelectorByWorkspaceId(workspaceId), (tasks) => getBoardRestorationTasksFromTasks(tasks));

export const makeRestoreLibraryTasksByWorkspaceId = (workspaceId?: string) =>
  createSelector(tasksSelectorByWorkspaceId(workspaceId), (tasks) => getLibraryRestorationTasksFromTasks(tasks));

export const makeRestoreAssetTasksByWorkspaceId = (workspaceId?: string) =>
  createSelector(tasksSelectorByWorkspaceId(workspaceId), (tasks) => getAssetRestorationTasksFromTasks(tasks));

export const makeImporterTasksSelectorByWorkspaceId = (workspaceId?: string) =>
  createSelector(tasksSelectorByWorkspaceId(workspaceId), (tasks) => getImporterTasksFromTasks(tasks));

export const makeAssetTransformationTasksSelectorByWorkspaceId = (workspaceId?: string) =>
  createSelector(tasksSelectorByWorkspaceId(workspaceId), (tasks) => getAssetTransformationTasksFromTasks(tasks));

export const makeAssetTaggingTasksSelectorByWorkspaceId = (workspaceId?: string) =>
  createSelector(tasksSelectorByWorkspaceId(workspaceId), (tasks) => getAssetTaggingTasksFromTasks(tasks));

export const makeAssetCustomFieldValueUpdateTasksSelectorByWorkspaceId = (workspaceId?: string) =>
  createSelector(tasksSelectorByWorkspaceId(workspaceId), (tasks) =>
    getAssetCustomFieldValueUpdateTasksFromTasks(tasks),
  );

export const makeAssetBoardMembershipUpdateTasksSelectorByWorkspaceId = (workspaceId?: string) =>
  createSelector(tasksSelectorByWorkspaceId(workspaceId), (tasks) =>
    getAssetBoardMembershipUpdateTasksFromTasks(tasks),
  );

export const makeTranscriptEditingTasksSelectorByWorkspaceId = (workspaceId?: string) =>
  createSelector(tasksSelectorByWorkspaceId(workspaceId), (tasks) => getTranscriptEditingTasksFromTasks(tasks));

export const isTranscriptEditingTaskInProgressForClipTranscriptIdSelector = createSelector(
  tasksSelector,
  (_: AirState, clipTranscriptId: string | undefined) => clipTranscriptId,
  (tasks, clipTranscriptId) => {
    if (!clipTranscriptId) {
      return false;
    }

    return getTranscriptEditingTasksFromTasks(tasks).some(
      (task) =>
        task.metadata.clipTranscriptId === clipTranscriptId &&
        (task.status === 'pending' || task.status === 'in-progress'),
    );
  },
);

export const boardIdsBeingDeletedSelector = createSelector(tasksSelector, (tasks) =>
  getBoardDeletionTasksFromTasks(tasks).flatMap((task) =>
    task.status === 'in-progress' ? task.metadata.boardIds : [],
  ),
);

export const libraryIdsBeingDeletedSelector = createSelector(tasksSelector, (tasks) =>
  getLibraryDeletionTasksFromTasks(tasks).flatMap((task) =>
    task.status === 'in-progress' ? task.metadata.libraryId : [],
  ),
);

export const boardIdsBeingRestoredSelector = createSelector(tasksSelector, (tasks) =>
  getBoardRestorationTasksFromTasks(tasks).flatMap((task) =>
    task.status === 'in-progress' ? task.metadata.boardId : [],
  ),
);

export const libraryIdsBeingRestoredSelector = createSelector(tasksSelector, (tasks) =>
  getLibraryRestorationTasksFromTasks(tasks).flatMap((task) =>
    task.status === 'in-progress' ? task.metadata.libraryId : [],
  ),
);

export const assetIdsBeingRestoredSelector = createSelector(tasksSelector, (tasks) =>
  getAssetRestorationTasksFromTasks(tasks).flatMap((task) =>
    task.status === 'in-progress' ? task.metadata.assets.map(({ id }) => id) : [],
  ),
);

export const importerTasksSelector = createSelector(tasksSelector, (tasks) => getImporterTasksFromTasks(tasks));

export const pendingImportTasksSelector = createSelector(importerTasksSelector, (tasks) =>
  tasks.filter((task) => task.status === 'pending' || task.status === 'in-progress'),
);

export const pendingImportTaskCountSelector = createSelector(pendingImportTasksSelector, (tasks) => tasks.length);

export const hasPendingImportTaskForAdIdSelector = createSelector(
  pendingImportTasksSelector,
  (_: AirState, adId: string) => adId,
  (tasks, adId) =>
    tasks.find((task) => {
      if (task.metadata?.source === 'meta') {
        const descriptor = getAdImportDescriptionMetadata(task.metadata);

        return descriptor?.id === adId;
      }

      return false;
    }),
);
